import { AfterViewInit, Component, ElementRef, Input, OnDestroy, ViewChild } from '@angular/core';
import flatpickr from 'flatpickr';
import { FormControl } from '@angular/forms';
import { icons } from 'src/assets/images/icons';
import { Subscription } from 'rxjs';

@Component({
  selector: 'app-date-picker',
  templateUrl: './date-picker.component.html',
  styleUrls: ['./date-picker.component.scss']
})
export class DatePickerComponent implements AfterViewInit, OnDestroy {
  public iconPack = icons;

  @Input() control: FormControl = new FormControl(); // Reactive form control
  @Input() class?: string | null = null; // Custom classes for styling
  @Input() label?: string | null = null; // Label text
  @Input() requiredText?: string | null = null; // Validation message
  @Input() placeholder?: string | null = null; // Placeholder text

  @ViewChild('dateInput', { static: true }) dateInput!: ElementRef; // Reference to the input element

  flatpickrInstance: any; // Flatpickr instance
  controlSubscription!: Subscription;

  ngAfterViewInit(): void {
    this.initializeFlatpickr();

    // Subscribe to changes in the FormControl that come from outside Flatpickr
    this.controlSubscription = this.control.valueChanges.subscribe(value => {
      if (this.flatpickrInstance && value) {
        const newDate = new Date(value);
        // Only update if the new date is different from the currently selected date.
        if (
          this.flatpickrInstance.selectedDates &&
          this.flatpickrInstance.selectedDates.length > 0 &&
          this.flatpickrInstance.selectedDates[0].getTime() === newDate.getTime()
        ) {
          return;
        }
        this.flatpickrInstance.setDate(newDate, false);
      }
    });
  }

  initializeFlatpickr(): void {
    // Create a normalized minDate (today with time set to midnight)
    const normalizedToday = new Date();
    normalizedToday.setHours(0, 0, 0, 0);

    // Use the full value for defaultDate (preserving the time) if it exists
    const initialDate = this.control.value ? new Date(this.control.value) : undefined;

    this.flatpickrInstance = flatpickr(this.dateInput.nativeElement, {
      dateFormat: 'd-m-Y',       // Flatpickr tokens for the displayed format
      altInput: true,            // Use an alternate input for a nicer display
      altFormat: 'd/m/Y',        // Human-readable format for the altInput
      allowInput: false,         // Disallow manual date entry
      minDate: normalizedToday,  // Only allow dates from today onward
      defaultDate: initialDate,  // Prepopulate with the full date/time from the control
      disableMobile: true,       // Always use Flatpickr instead of the native picker on mobile

      onReady: (selectedDates: Date[], dateStr: string, instance: any) => {
        (instance.altInput as HTMLInputElement).placeholder = this.placeholder || 'Select Date';
      },
      onChange: (selectedDates: Date[]) => {
        if (selectedDates && selectedDates.length > 0) {
          // Update control without emitting a change event to avoid recursive updates.
          this.control.setValue(selectedDates[0], { emitEvent: false });
        }
      },
      onClose: () => {
        this.control.markAsDirty();
        this.control.markAsTouched();
      }
    });
  }

  ngOnDestroy(): void {
    if (this.flatpickrInstance) {
      this.flatpickrInstance.destroy(); // Clean up the Flatpickr instance
    }
    if (this.controlSubscription) {
      this.controlSubscription.unsubscribe();
    }
  }

  onClearDate(): void {
    this.flatpickrInstance.clear();
    this.flatpickrInstance.open();
    this.control.reset();
  }
}
